"use client";
import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { Menu, X, User2 } from "lucide-react";

// Configurable section links
const LINKS = [
    { href: "/#hero", label: "Home" },
    { href: "/#courses", label: "Courses" },
    { href: "/#events", label: "Events" },
    { href: "/#testimonial", label: "Testimonials" },
    { href: "/#about", label: "About" },
    { href: "/#contact", label: "Contact" }
];

// Simple throttle to avoid excessive re-renders on scroll
function throttle(fn, wait) {
    let last = 0;
    return (...args) => {
        const now = Date.now();
        if (now - last >= wait) {
            last = now;
            fn(...args);
        }
    };
}

export default function NavbarClient({ logo }) {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [activeId, setActiveId] = useState("hero");

    const onScroll = useCallback(
        throttle(() => {
            const y = window.scrollY;
            setScrolled(y > 12);
        }, 100),
        []
    );

    useEffect(() => {
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [onScroll]);

    useEffect(() => {
        if (open) {
            document.documentElement.style.overflow = "hidden";
        } else {
            document.documentElement.style.overflow = "";
        }
    }, [open]);

    // Close on hash navigation
    useEffect(() => {
        const handler = () => setOpen(false);
        window.addEventListener("hashchange", handler);
        return () => window.removeEventListener("hashchange", handler);
    }, []);

    // Active section observer
    useEffect(() => {
        const ids = LINKS.map(l => l.href.slice(1));
        const observer = new IntersectionObserver((entries) => {
            const visible = entries.filter(e => e.isIntersecting).sort((a,b)=> b.intersectionRatio - a.intersectionRatio)[0];
            if (visible) setActiveId(visible.target.id);
        }, {
            root: null,
            threshold: [0.15, 0.3, 0.45, 0.6, 0.75],
            rootMargin: "-10% 0px -55% 0px"
        });
        ids.forEach(id => { const el = document.getElementById(id); if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl">
            <div
                className={`transition-all bg-white/5 shadow-sm ${scrolled ? 'border-b border-white/10' : 'border-transparent'}`}
                data-scrolled={scrolled}
            >
                <div className="max-w-7xl mx-auto  px-4">
                    <div className="h-20 flex items-center justify-between gap-6 transition-all">
                        {/* Brand - Server-rendered logo */}
                        {logo}
                        
                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center gap-7 text-sm font-medium">
                            {LINKS.map(l => {
                                const isActive = activeId === l.href.slice(1);
                                return (
                                    <Link
                                        key={l.href}
                                        href={l.href}
                                        aria-current={isActive ? 'true' : undefined}
                                        className={`relative text-white group py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded flex items-center gap-1.5 ${isActive ? 'text-brand' : 'text-brand-alt hover:text-brand'} transition-colors`}
                                    >
                                        <span className="text-white">{l.label}</span>
                                        <span className={`pointer-events-none absolute left-0 -bottom-1 h-[2px] rounded bg-brand-gradient transition-all duration-400 ${isActive ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                                    </Link>
                                );
                            })}
                            <Link href="/enroll" className="relative inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs font-semibold text-white bg-brand-gradient hover:brightness-110 shadow shadow-brand-glow transition">
                                <User2 className="w-4 h-4" />
                                Enroll Now
                            </Link>
                        </nav>
                        
                        {/* Mobile toggle */}
                        <button
                            onClick={() => setOpen(o => !o)}
                            aria-label={open ? 'Close menu' : 'Open menu'}
                            aria-expanded={open}
                            className="md:hidden relative z-50 p-2 rounded-full text-brand focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                        >
                            <span className="block text-white bold transition-transform duration-300">
                                {open ? <X className="w-8 h-8 text-black" /> : <Menu className="w-8 h-8" />}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Mobile overlay */}
                {open && (
                    <div className="fixed inset-0 bg-white/95 backdrop-blur-xl md:hidden flex flex-col animate-fadeIn">
                        <nav className="mt-20 pt-6 px-6 bg-white flex flex-col gap-6" aria-label="Mobile">
                            {LINKS.map((l) => {
                                const isActive = activeId === l.href.slice(1);
                                return (
                                    <a
                                        key={l.href}
                                        href={l.href}
                                        onClick={() => setOpen(false)}
                                        className={`text-lg font-medium bg-white tracking-tight relative pl-1 flex items-center gap-2 text-black transition-colors`}
                                    >
                                        {l.label}
                                        <span className={`absolute inset-x-0 -bottom-1 h-px bg-gradient-to-r from-[var(--brand)]/0 via-[var(--brand)]/70 to-[var(--brand)]/0 ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`} />
                                    </a>
                                );
                            })}
                            <Link
                                href="/enroll"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center gap-2 mt-4 rounded-full h-12 px-6 font-semibold text-sm mb-4 bg-brand-gradient text-white shadow-lg shadow-brand-glow hover:brightness-110"
                            >
                                <User2 className="w-5 h-5" />
                                Enroll Now
                            </Link>
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
